/**
 * Centralized validation utilities for forms
 */

/**
 * Check if a value is present (not empty/null/undefined)
 * @param {string} value
 * @param {string} fieldName - Optional field name for error message
 * @returns {string|null} Error message or null if valid
 */
export const required = (value, fieldName = 'This field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

/**
 * Check minimum length
 * @param {string} value
 * @param {number} min
 * @param {string} fieldName
 * @returns {string|null}
 */
export const minLength = (value, min, fieldName = 'This field') => {
  if (!value || value.length < min) {
    return `${fieldName} must be at least ${min} characters`;
  }
  return null;
};

/**
 * Check maximum length
 * @param {string} value
 * @param {number} max
 * @param {string} fieldName
 * @returns {string|null}
 */
export const maxLength = (value, max, fieldName = 'This field') => {
  if (value && value.length > max) {
    return `${fieldName} must be no more than ${max} characters`;
  }
  return null;
};

/**
 * Validate email format
 * @param {string} value
 * @returns {string|null}
 */
export const email = (value) => {
  if (!value) {
    return 'Email is required';
  }
  // RFC 5322 simplified pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value.trim())) {
    return 'Please enter a valid email address';
  }
  return null;
};

/**
 * Validate number is within range
 * @param {string|number} value
 * @param {number} min
 * @param {number} max
 * @param {string} fieldName
 * @returns {string|null}
 */
export const numberRange = (value, min, max, fieldName = 'Value') => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) {
    return `${fieldName} must be a number`;
  }
  if (num < min || num > max) {
    return `${fieldName} must be between ${min} and ${max}`;
  }
  return null;
};

/**
 * Minimum password length enforced in the UI.
 *
 * The Supabase project's own rule is 6 (Auth → Providers → Email → "Minimum
 * password length"), with no required character classes but "Prevent use of
 * leaked passwords" (HaveIBeenPwned) turned ON. We keep the on-screen minimum
 * at 8 — stricter than the server floor, and what Supabase itself recommends —
 * so anything that passes here also passes the server length check. The
 * leaked-password check can only run server-side, so a password that clears
 * this rule can still be rejected at account creation; that rejection is now
 * surfaced inline on the password step (see OnboardingFlow) instead of after
 * the whole questionnaire.
 */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Password strength validation
 * Rules:
 * - Minimum PASSWORD_MIN_LENGTH characters
 *
 * Deliberately no mandatory composition rules (uppercase/number/symbol) —
 * current guidance (NIST 800-63B) favours length over enforced complexity,
 * since composition rules push users toward predictable patterns or
 * abandoning signup without adding real resistance to guessing. Matches
 * Nutrition's existing rule and the Supabase project config (no required
 * characters), which are both length-only.
 *
 * @param {string} value
 * @returns {string|null} First failing rule message or null if valid
 */
export const passwordStrength = (value) => {
  if (!value) {
    return 'Password is required';
  }

  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }

  return null;
};

/**
 * Check if two values match (e.g., password confirmation)
 * @param {string} value
 * @param {string} matchValue
 * @param {string} fieldName
 * @returns {string|null}
 */
export const matches = (value, matchValue, fieldName = 'Values') => {
  if (value !== matchValue) {
    return `${fieldName} do not match`;
  }
  return null;
};

/**
 * Validate password confirmation
 * @param {string} password
 * @param {string} confirmPassword
 * @returns {string|null}
 */
export const passwordsMatch = (password, confirmPassword) => {
  if (!confirmPassword) {
    return 'Please confirm your password';
  }
  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }
  return null;
};

/**
 * Run multiple validators on a value, return first error
 * @param {*} value
 * @param  {...Function} validators - Array of validator functions
 * @returns {string|null}
 */
export const validate = (value, ...validators) => {
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return null;
};

/**
 * Get password requirements as an array for UI display
 * @param {string} value - Current password value
 * @returns {Array<{label: string, met: boolean}>}
 */
export const getPasswordRequirements = (value = '') => {
  return [
    { label: `At least ${PASSWORD_MIN_LENGTH} characters`, met: value.length >= PASSWORD_MIN_LENGTH },
  ];
};

/**
 * Check if password meets all requirements
 * @param {string} value
 * @returns {boolean}
 */
export const isPasswordValid = (value) => {
  return passwordStrength(value) === null;
};

/**
 * Turn a Supabase Auth signUp error into something we can show the user on the
 * right step of onboarding, so account problems are caught at the password
 * screen instead of after the whole questionnaire.
 *
 * @param {{message?: string, code?: string, status?: number, name?: string}} err
 * @returns {{ field: 'password'|'email'|'confirmPassword', message: string, code: string|null }}
 */
export const friendlyAuthError = (err) => {
  const raw = (err && err.message) || 'Something went wrong creating your account.';
  const m = raw.toLowerCase();
  const code = (err && (err.code || err.name)) || null;

  // Email already has an account
  if (
    code === 'user_already_exists' ||
    code === 'email_exists' ||
    m.includes('already registered') ||
    m.includes('already been registered') ||
    m.includes('user already exists')
  ) {
    return {
      field: 'email',
      code: 'already_registered',
      message: 'This email already has an account. Log in instead, or use a different email.',
    };
  }

  // Leaked / weak password (HaveIBeenPwned check is enabled on the project)
  if (
    code === 'weak_password' ||
    err?.name === 'AuthWeakPasswordError' ||
    m.includes('known to be weak') ||
    m.includes('easy to guess') ||
    m.includes('pwned') ||
    m.includes('data breach') ||
    m.includes('compromised')
  ) {
    return {
      field: 'password',
      code: 'weak_password',
      message: 'This password has shown up in a known data breach. Please choose a different one.',
    };
  }

  // Too short / other password rule
  if (m.includes('password') && (m.includes('should') || m.includes('at least') || m.includes('characters'))) {
    return { field: 'password', code: 'password_rule', message: raw };
  }

  // Invalid email format
  if (m.includes('email') && (m.includes('invalid') || m.includes('valid'))) {
    return { field: 'email', code: 'invalid_email', message: 'That email address doesn’t look right. Please check it.' };
  }

  // Rate limiting
  if (code === 'over_request_rate_limit' || m.includes('rate limit') || m.includes('too many')) {
    return { field: 'password', code: 'rate_limited', message: 'Too many attempts just now. Wait a minute and try again.' };
  }

  // Network / unknown — let them retry in place
  return { field: 'password', code: null, message: 'Couldn’t create your account just now. Check your connection and try again.' };
};
